'use client';

import { requestBetaAccess } from '@actions/request-beta-access';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger
} from '@components/ui/drawer';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Textarea } from '@components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@utils/cn';
import { AlertCircle, ArrowRight, CheckCircle } from 'lucide-react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  email: z
    .string({ error: 'Email is required' })
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email address format' }),
  company: z.string().optional()
});

type FormValues = z.infer<typeof formSchema>;

type RequestAccessDrawerProps = {
  trigger?: ReactNode;
  presentation?: 'default' | 'dark';
};

export function RequestAccessDrawer({
  trigger,
  presentation = 'default'
}: RequestAccessDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formResponse, setFormResponse] = useState<{
    success: boolean;
    message: string;
    rateLimited?: boolean;
    retryAfter?: number | null;
  } | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const rateLimitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', company: '' }
  });

  useEffect(() => {
    if (formResponse?.rateLimited && formResponse.retryAfter) {
      const now = Date.now();
      const retryAfter = formResponse.retryAfter;

      if (retryAfter > now) {
        setIsRateLimited(true);

        if (rateLimitTimeoutRef.current) {
          clearTimeout(rateLimitTimeoutRef.current);
        }

        rateLimitTimeoutRef.current = setTimeout(() => {
          setIsRateLimited(false);
        }, retryAfter - now);
      }
    }

    return () => {
      if (rateLimitTimeoutRef.current) {
        clearTimeout(rateLimitTimeoutRef.current);
      }
    };
  }, [formResponse]);

  async function onSubmit(data: FormValues) {
    setIsSubmitting(true);
    setFormResponse(null);

    try {
      const formData = new FormData();
      formData.append('email', data.email);
      formData.append('company', data.company || '');

      const response = await requestBetaAccess(null, formData);
      setFormResponse(response);

      if (response.success) {
        form.reset();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormResponse({
        success: false,
        message: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Drawer>
      <DrawerTrigger asChild>
        {trigger ?? (
          <Button>
            Request access
            <ArrowRight />
          </Button>
        )}
      </DrawerTrigger>
      <DrawerContent
        className={cn(
          'max-h-[92dvh] overflow-y-auto',
          presentation === 'dark' &&
            'dark border-border bg-background text-foreground'
        )}
      >
        <div className="mx-auto w-full max-w-md">
          <DrawerHeader>
            <DrawerTitle>Request LQD access</DrawerTitle>
            <DrawerDescription>
              Tell us where you work. We’ll follow up with the right next step
              for your operation.
            </DrawerDescription>
          </DrawerHeader>

          <div className="p-4 pb-0">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
                noValidate
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder="you@restaurant.com"
                          {...field}
                          disabled={isSubmitting || isRateLimited}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Restaurant or company</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Optional: Tell us about your operation"
                          className="min-h-24 resize-y"
                          {...field}
                          disabled={isSubmitting || isRateLimited}
                        />
                      </FormControl>
                      <FormDescription>
                        A few details help us understand what your team needs.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {formResponse && (
                  <Alert
                    variant={formResponse.success ? 'default' : 'destructive'}
                    aria-live="polite"
                  >
                    <div className="flex items-center gap-2">
                      {formResponse.success ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <AlertCircle className="h-4 w-4" />
                      )}
                      <AlertDescription>
                        {formResponse.message}
                      </AlertDescription>
                    </div>
                  </Alert>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting || isRateLimited}
                  className="w-full"
                >
                  {isSubmitting ? 'Sending request…' : 'Request access'}
                </Button>
              </form>
            </Form>
          </div>

          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="ghost" className="w-full">
                Cancel
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
